import {useLocation} from 'react-router-dom';
import {PAGE_TITLE_ACCENTS, PAGE_TITLES} from '@app/router/page-titles';
import {useActiveWorkspace} from '@api/workspaces';
import {BRAND, IS_GMK} from '@lib/brand';

/** Страницы, которые рендерят заголовок сами (в одном компоненте с формой) —
 *  Topbar для них только выставляет `document.title`, видимый `<h1>` не рисует. */
const SELF_TITLED_PAGES = new Set(['/settings/workspace']);

/** То же для разделов с параметром в пути: карточка клиента — заголовок номером. */
const SELF_TITLED_PREFIXES = ['/clients/'];

/**
 * Заголовок страницы. С раунда 31 живёт в нормальном потоке (НЕ fixed) —
 * скроллится вместе с контентом. document.title каждая страница ставит сама
 * через `useDocumentTitle` — здесь только видимый H1.
 *
 * Стиль брендбука GMK: акцидентный шрифт капсом в две строки (вторая — серая),
 * справа — подпись воркспейса, как «GUIDELINE» на слайдах. В оформлении
 * оригинального Colver — заголовок одной строкой, как в продукте.
 */
export const Topbar = () => {
  const {pathname, search} = useLocation();
  const {active} = useActiveWorkspace();

  const title = PAGE_TITLES[pathname] ?? BRAND.productName;
  const accent = PAGE_TITLE_ACCENTS[pathname];

  if (
    SELF_TITLED_PAGES.has(pathname) ||
    SELF_TITLED_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
    (pathname === '/dashboard' &&
      new URLSearchParams(search).get('notificationSid'))
  )
    return null;

  if (!IS_GMK) {
    // Оригинальный Colver: заголовок одной строкой, без подписи воркспейса.
    return (
      <header
        className='w-full flex flex-col bg-[var(--color-background)]'
        style={{minHeight: 'var(--topbar-h)'}}
      >
        <h1 className='text-2xl font-bold text-[var(--color-foreground)] leading-tight truncate'>
          {title}
        </h1>
      </header>
    );
  }

  return (
    <header
      className='w-full flex items-start justify-between gap-6'
      style={{minHeight: 'var(--topbar-h)'}}
    >
      <h1 className='font-heading uppercase font-medium text-[28px] leading-[1.05] tracking-[0.01em] text-[var(--color-foreground)] min-w-0'>
        <span className='block truncate'>{title}</span>
        {accent && (
          <span className='block truncate text-[var(--brand-gray-mid2)]'>
            {accent}
          </span>
        )}
      </h1>
      {active?.name && (
        <span className='hidden md:block pt-1 font-heading uppercase text-[13px] tracking-[0.04em] text-[var(--brand-gray-mid)] truncate max-w-[40%]'>
          {active.name}
        </span>
      )}
    </header>
  );
};
